import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SearchPage from '@/components/Pages/SearchPage';
import { useModelStore, useCompanyStore, useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import ListItem from '@/components/ListItem';
import { useTranslation } from '@/hooks/useTranslation';

const styles = StyleSheet.create({
  emptyRoot: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  emptyIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 20,
    textAlign: 'center',
  },
  emptySubtitle: {
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.9,
  },
});

const ModelSearchPage = React.memo(() => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();

  // Get models from store
  const searchResults = useModelStore(state => state.searchResults);
  const isSearching = useModelStore(state => state.isSearching);
  const searchError = useModelStore(state => state.modelError);
  const searchModels = useModelStore(state => state.searchModels);
  const clearSearch = useModelStore(state => state.clearSearch);
  const { openModal } = useModalStore(state => state.actions);

  const companies = useCompanyStore(state => state.companies);

  // Memoize badges creation
  const createModelBadges = useCallback((model) => {
    const badges = [];
    
    if (model.flags?.isPlusOnly) {
      badges.push({
        text: t('pages.models.plus'),
        isPlus: true
      });
    }

    if (model.flags?.isOpenSource) {
      badges.push({
        text: t('pages.models.openSource'),
        backgroundColor: colors.success_color_primary,
        textColor: 'success'
      });
    }

    return badges;
  }, [colors, t]);

  const handleDetailsModal = useCallback((modelId) => {
    openModal(
      'ModelDetailsModal',
      {
        key: `model-details-${modelId}`,
        modelId,
      },
      { 
        width: isXS ? '100%' : 600, 
        fullScreen: isXS
      }
    );
  }, [openModal, isXS]);


  const renderModelItem = useCallback((model) => {
    if (!model) return null;
    const company = model.companyId ? companies[model.companyId] : null;

    const badges = createModelBadges(model);
    
    const subtitle = (
      <Text color='secondary' size='m' numberOfLines={1}>
        {company?.name ? `${company.name} • ` : ''}{model.description}
      </Text>
    );

    return (
      <ListItem
        key={model.id}
        isSquare={true}
        imageUrl={company?.logoPath}
        iconName="cube"
        title={model.name}
        subtitle={subtitle}
        badges={badges}
        isXS={isXS}
        onPress={() => handleDetailsModal(model.id)}
      />
    );
  }, [companies, isXS, createModelBadges, handleDetailsModal]);

  const renderEmpty = useCallback(() => {
    const iconSize = 40;
    const iconColor = colors.text_color_tertiary;
    return (
      <View style={[styles.emptyRoot, { paddingVertical: 48 }]}>
        <View style={[styles.emptyIconWrap, { backgroundColor: colors.solid_color_tertiary }]}>
          <Icon name="magnifying-glass" size={iconSize} color={iconColor} />
        </View>
        <Text
          size="l"
          weight="semibold"
          color="primary"
          style={styles.emptyTitle}
        >
          {t('pages.models.searchEmptyTitle')}
        </Text>
        <Text
          size="m"
          color="secondary"
          style={styles.emptySubtitle}
        >
          {t('pages.models.searchEmptySubtitle')}
        </Text>
      </View>
    );
  }, [colors, t]);

  const searchPageProps = useMemo(() => ({
    title: t('pages.models.searchTitle'),
    description: "Search through our collection of AI models. Find models by name or provider. Explore different AI models to enhance your experience.",
    searchPlaceholder: t('pages.models.searchPlaceholderFull'),
    cancelRoute: "/models",
    isSearching,
    searchError,
    searchResults,
    onSearch: searchModels,
    onClearSearch: clearSearch,
    renderItem: renderModelItem,
    emptyMessage: t('pages.models.searchEmptyMessage'),
    renderEmpty,
  }), [isSearching, searchError, searchResults, searchModels, clearSearch, renderModelItem, renderEmpty, t]);

  return <SearchPage {...searchPageProps} />;
});

function ModelSearchRoute() {
  return (
    <ModelSearchPage />
  );
}

export default React.memo(ModelSearchRoute);